/**
 * Content-sync event bridge
 *
 * A tiny, universal broadcast layer that keeps star / archive / delete state
 * synchronized across every surface that renders the same starrable items:
 * the sidebar (`SidebarRecentsController`), `/search` (`SessionsSection`),
 * `/notes` (`PartnerNotesSection`), `/discovery` Open Threads
 * (`DiscoverySection`), `/experiments` (`PracticesSection`), and the `/insights`
 * guides tab (`InsightsSection`).
 *
 * Five single-item events (payloads keep the historical `content:starred`
 * shape so existing listeners stay valid). **All flags are booleans on the
 * wire** — each surface translates to its own storage field (boolean vs
 * timestamp `starredAt`/`archivedAt`/`dismissedAt`/`viewedAt`/`flaggedAt`; see
 * each listener):
 *
 *   - `content:starred`   → `{ itemType, itemId, starred }`
 *   - `content:archived`  → `{ itemType, itemId, archived }`
 *   - `content:deleted`   → `{ itemType, itemId }`
 *   - `content:viewed`    → `{ itemType, itemId, viewed }`
 *   - `content:flagged`   → `{ itemType, itemId, flagged }`
 *   - `content:activated` → `{ itemType, itemId }`
 *
 * `itemType ∈ { 'session', 'note', 'thread', 'practice', 'guide', 'sharedGuide' }`
 * for the first three; `content:viewed` covers all of those except `'thread'` (open
 * threads have no viewed state) — it drives unread presentation wherever those
 * items render, including the sidebar's recents rows.
 * `content:activated` is `'practice'`-only: a draft practice flipping to
 * `status: 'active'` on accept. It carries no boolean because activation is
 * one-directional (a practice is never de-activated back to draft) — it exists
 * so `BadgeService` can reconcile the Practices count, which is otherwise blind
 * to acceptance. `content:flagged` is `'session'`-only — sessions use a manually armed unread
 * marker that clears on detail-open, so `SessionNote.flaggedAt` (not
 * `viewedAt`) drives their sidebar indicator; `content:viewed` still
 * fires for sessions too (their `viewedAt` auto-stamp-on-open keeps writing,
 * see sessionNotes.js), it's just UI-dead there post-plan. `itemId` is
 * consistent across surfaces (SessionNote row id / PartnerMemo id /
 * OpenThread UUID / guideDefinitionId / Practice row id).
 *
 * `content:viewed` is bidirectional: every surface emits after confirmed
 * persistence, while sidebar/native caches and BadgeService subscribe to keep
 * dots, pills, and viewed guards synchronized. `content:flagged` follows the
 * same cross-surface pattern for sessions. Handlers patch local state only and
 * never re-emit, so the bridge has no event loop.
 *
 * **Why the helpers earn their place:** `eventBus.emit` is an unguarded
 * synchronous `forEach` (`utils.js`), so a bug in *any* content listener would
 * throw straight up into the emitting mutation method's `try/catch` and make a
 * **successful** backend mutation look failed (error toast / reverted optimistic
 * flip). `safeEmit` wraps `emit` in try/catch and reports rather than rethrows,
 * so a listener bug can never corrupt a mutation flow. It does **not** swallow
 * the mutation's own errors — those still flow through each call site's own
 * `try/catch`.
 *
 * @module core/contentSync
 */

import { eventBus } from './utils.js';

export const CONTENT_STARRED = 'content:starred';
export const CONTENT_ARCHIVED = 'content:archived';
export const CONTENT_DELETED = 'content:deleted';
export const CONTENT_VIEWED = 'content:viewed';
export const CONTENT_FLAGGED = 'content:flagged';
export const CONTENT_ACTIVATED = 'content:activated';

/**
 * Apply one content-sync event to a collection of normalized content items.
 * The input collection, matching item, and nested payload are never mutated.
 * Thread archives map to `payload.dismissedAt`, practice archives map to
 * `payload.status`, and guides intentionally omit `payload.viewedAt`.
 *
 * @param {Array<Object>|null|undefined} collection
 * @param {string} event - One of the five normalized-item mutation events.
 * @param {{itemType?: string, itemId?: string, starred?: boolean, archived?: boolean, viewed?: boolean, flagged?: boolean}} payload
 * @returns {{collection: Array<Object>|null|undefined, changed: boolean}}
 */
export function reduceNormalizedItems(collection, event, payload = {}) {
  if (!Array.isArray(collection)) return { collection, changed: false };

  const matches = (item) => (
    item.type === payload.itemType && String(item.id) === String(payload.itemId)
  );

  if (event === CONTENT_DELETED) {
    const nextCollection = collection.filter(item => !matches(item));
    return nextCollection.length === collection.length
      ? { collection, changed: false }
      : { collection: nextCollection, changed: true };
  }

  const itemIndex = collection.findIndex(matches);
  if (itemIndex === -1) return { collection, changed: false };

  const currentItem = collection[itemIndex];
  const stateField = {
    [CONTENT_STARRED]: 'starred',
    [CONTENT_ARCHIVED]: 'archived',
    [CONTENT_VIEWED]: 'viewed',
    [CONTENT_FLAGGED]: 'flagged'
  }[event];

  if (!stateField || currentItem[stateField] === payload[stateField]) {
    return { collection, changed: false };
  }

  const nextItem = { ...currentItem, [stateField]: payload[stateField] };
  const nextPayload = currentItem.payload ? { ...currentItem.payload } : null;

  if (event === CONTENT_STARRED) {
    nextItem.starredAt = payload.starred ? new Date().toISOString() : null;
    if (nextPayload) nextPayload.starredAt = nextItem.starredAt;
  } else if (event === CONTENT_ARCHIVED) {
    nextItem.archivedAt = payload.archived ? new Date().toISOString() : null;
    if (nextPayload) {
      if (currentItem.type === 'thread') nextPayload.dismissedAt = nextItem.archivedAt;
      else if (currentItem.type === 'practice') nextPayload.status = payload.archived ? 'archived' : 'active';
      else nextPayload.archivedAt = nextItem.archivedAt;
    }
  } else if (event === CONTENT_VIEWED && nextPayload && currentItem.type !== 'guide') {
    nextPayload.viewedAt = payload.viewed ? new Date().toISOString() : null;
  } else if (event === CONTENT_FLAGGED && nextPayload) {
    nextPayload.flaggedAt = payload.flagged ? new Date().toISOString() : null;
  }

  if (nextPayload) nextItem.payload = nextPayload;
  const nextCollection = [...collection];
  nextCollection[itemIndex] = nextItem;
  return { collection: nextCollection, changed: true };
}

/**
 * Emit a content event, isolating any listener exception so it can never
 * propagate back into the emitting mutation flow.
 * @param {string} event
 * @param {Object} payload
 */
function safeEmit(event, payload) {
  try {
    eventBus.emit(event, payload);
  } catch (err) {
    console.error(`[contentSync] listener threw for ${event}:`, err);
  }
}

/**
 * Broadcast a star/unstar of a single item.
 * @param {'session'|'note'|'thread'|'practice'} itemType
 * @param {string} itemId
 * @param {boolean} starred
 */
export function emitContentStarred(itemType, itemId, starred) {
  safeEmit(CONTENT_STARRED, { itemType, itemId, starred });
}

/**
 * Broadcast an archive/unarchive of a single item.
 *
 * A `/library` guide card can carry two halves, and archive is per half: the
 * viewer's own reading emits `guide`, the couple's shared artifact emits
 * `sharedGuide`, both keyed by guideDefinitionId. A card that archives both
 * halves emits twice.
 * @param {'session'|'note'|'thread'|'practice'|'guide'|'sharedGuide'} itemType
 * @param {string} itemId
 * @param {boolean} archived
 */
export function emitContentArchived(itemType, itemId, archived) {
  safeEmit(CONTENT_ARCHIVED, { itemType, itemId, archived });
}

/**
 * Broadcast a delete of a single item.
 * @param {'session'|'note'|'thread'|'practice'} itemType
 * @param {string} itemId
 */
export function emitContentDeleted(itemType, itemId) {
  safeEmit(CONTENT_DELETED, { itemType, itemId });
}

/**
 * Broadcast a viewed/un-viewed change for a single item — feeds unread
 * presentation across native cards and both sidebar sections. Fire only
 * after the underlying mutation is confirmed successful (never broadcast an
 * unconfirmed mutation).
 * `sharedGuide` is the couple's half of a `/library` guide card (keyed by
 * guideDefinitionId, like `guide`); the two halves carry their own unread
 * state and clear independently.
 * @param {'session'|'note'|'guide'|'sharedGuide'|'practice'} itemType
 * @param {string} itemId
 * @param {boolean} viewed
 */
export function emitContentViewed(itemType, itemId, viewed) {
  safeEmit(CONTENT_VIEWED, { itemType, itemId, viewed });
}

/**
 * Broadcast a session manual-unread change — feeds the session-row indicator
 * in both sidebar sections while the UI presents it as Mark as read/unread.
 * Fire only after the underlying mutation is confirmed successful.
 * @param {'session'} itemType
 * @param {string} itemId
 * @param {boolean} flagged
 */
export function emitContentFlagged(itemType, itemId, flagged) {
  safeEmit(CONTENT_FLAGGED, { itemType, itemId, flagged });
}

/**
 * Broadcast that an existing draft item became active — today only an accepted
 * practice draft flipping to `status: 'active'`. Nothing is created; the row
 * already existed. Fire only after the mutation is confirmed successful.
 * @param {'practice'} itemType
 * @param {string} itemId
 */
export function emitContentActivated(itemType, itemId) {
  safeEmit(CONTENT_ACTIVATED, { itemType, itemId });
}
